/**
 * Types et interfaces pour la structure hiérarchique des WODs
 * Équivalent TypeScript du modèle Flutter wod_models.dart et wod_structure.dart
 */

export enum WodType {
  FOR_TIME = 'FOR TIME',
  AMRAP = 'AMRAP',
  EMOM = 'EMOM',
  TABATA = 'TABATA',
  MAX_LOAD = 'MAX LOAD'
}

// 🆕 Types pour WODs Multi-Parties
export enum PartType {
  FOR_TIME = 'for_time',
  AMRAP = 'amrap',
  MAX_LOAD = 'max_load',
  REST = 'rest'
}

// 🆕 Mode d'exécution des parties (2025-11-30)
export enum ExecutionMode {
  SEQUENTIAL = 'sequential', // Parties une après l'autre (défaut)
  PARALLEL = 'parallel'      // Parties simultanées
}

// 🆕 Mode de tracking des parties (2025-11-30)
export enum TrackingMode {
  LIVE = 'live',     // Scoring temps réel par le juge (défaut)
  MANUAL = 'manual'  // Saisie manuelle à la fin du heat
}

export interface WodPart {
  part_number: number;
  name: string;
  type: PartType;
  time_cap_minutes?: number;
  duration_seconds?: number; // Pour les pauses (type='rest')
  description?: string;
  max_points?: number; // Points attribués pour cette partie
  scoring_type?: string; // 'time', 'reps', 'weight'
  is_mandatory?: boolean; // Pour les pauses
  structure?: WodStructure; // Structure hiérarchique spécifique à cette partie
  // 🆕 Support parties parallèles (2025-11-30)
  execution_mode?: ExecutionMode; // 'sequential' (défaut) ou 'parallel'
  tracking_mode?: TrackingMode;   // 'live' (défaut) ou 'manual'
  parallel_group?: number;         // Parties avec même numéro = simultanées
  // 🆕 Configuration Max Load (2025-12-28)
  max_load_reps?: number;          // Nombre de tentatives (ex: 3 pour "3 Clean and Jerk")
  max_load_scoring_mode?: 'max' | 'total'; // 'max' = meilleur poids, 'total' = somme des poids réussis
  team_total?: boolean; // charge comptée dans le total de l'équipe (classement sur la somme des parties)
}

export enum ScoringMethod {
  TIME = 'time',
  REPS = 'reps',
  ROUNDS = 'rounds',
  LOAD = 'load'
}

export enum WodElementType {
  MOVEMENT = 'movement',
  GROUP = 'group'
}

export interface ScoringConfig {
  defaultMethod: ScoringMethod;
  availableMethods: ScoringMethod[];
  tiebreaker?: ScoringMethod;
  allowPartialScores: boolean;
  timeCap?: number; // en minutes
}

// Base interface pour tous les éléments de structure
export interface WodElement {
  id: string;
  type: WodElementType;
}

// Mouvement individuel (feuille de l'arbre)
export interface WodMovement extends WodElement {
  type: WodElementType.MOVEMENT;
  description: string; // "Thrusters", "Pull-ups"
  reps?: number; // 10, 15, 21
  notes?: string; // "43kg", "strict", "chest to bar"
  isTiebreak?: boolean; // 🆕 NOUVEAU: Indique si ce mouvement est utilisé comme tiebreak pour départager les égalités
  // Prescription (docs/features/athlete-workout-model.md) — mêmes clés que
  // le générateur WODEE et l'app Athlète.
  isMaxReps?: boolean; // « max pull-ups » : le nombre de reps est le score, pas la prescription
  weight_kg?: number; // charge prescrite (hommes, ou unique)
  weight_kg_women?: number;
  percentage_rm?: number; // « 80 % du 1RM » — un pourcentage, jamais des kg
  rm_reference?: string; // si le % vise un AUTRE mouvement (« 30 % du 1RM Deadlift »)
}

/**
 * Ladder (LOG-220) : suite de reps SANS FIN portée par un GROUPE, au même rang
 * que `isAmrap` et `repetitions` — « 1-2-3… », « 3-6-9… ». Les `reps` des
 * enfants sont un MULTIPLICATEUR du barreau (défaut 1) : au tour r, chaque
 * mouvement fait `reps × barreau(r)`. Un ladder se grimpe jusqu'au time cap :
 * c'est un AMRAP. Une suite bornée (« 3-6-9-…-21 », « 10-9-8…1 ») n'est PAS un
 * ladder : c'est une liste de tours, écrite en entier (décision 2026-09-10).
 * Même sémantique que `progression_calculator.py` (local-api).
 */
export interface LadderConfig {
  start: number;
  increment: number;
}

// Groupe de mouvements (nœud de l'arbre)
export interface WodGroup extends WodElement {
  type: WodElementType.GROUP;
  repetitions: number; // 3 (pour "3 rounds"), 1 (pour groupe sans répétition)
  isAmrap: boolean; // true = AMRAP, false = rounds normaux
  name?: string; // "Warm-up", "Main WOD", optionnel
  ladder?: LadderConfig | null; // schéma de reps progressives (le ladder EST le schéma de tours)
  children: WodElement[]; // ⭐ Récursif - peut contenir mouvements ou sous-groupes
}

// Structure complète du WOD
export interface WodStructure {
  elements: WodElement[]; // Liste racine des éléments
}

// WOD complet
export interface WOD {
  id?: string;
  name: string;
  event_id: string;
  wod_number: number;
  type: WodType;
  description: string;
  time_cap?: number; // minutes
  scoring_type: 'time' | 'reps' | 'weight'; // Type de scoring
  instructions?: string;
  movements: string[]; // Liste plate pour compatibilité (générée depuis structure)
  structure?: WodStructure; // ⭐ Structure hiérarchique optionnelle
  scoring_config?: ScoringConfig;
  difficulty_level?: string; // "RX", "Scaled", "Masters", etc.
  created_at?: string;
  updated_at?: string;
  // 🆕 Support WODs Multi-Parties avec variations par catégorie
  parts?: Record<string, WodPart[]>; // Dict { "default": [...], "category_id": [...] }
  /** Ordre d'affichage des clés de `parts` (les clés d'un JSONB n'en ont pas). */
  variant_order?: string[] | null;
  status?: 'active' | 'inactive'; // Statut du WOD (défaut: active)
  // Paliers de divulgation : Brouillon → Mystère → Annoncé → Public. Le palier n'est pas
  // stocké, il se déduit de ces deux dates — le serveur le renvoie calculé.
  palier?: 'draft' | 'mystery' | 'announced' | 'public';
  mystery_at?: string | null; // ISO8601 UTC ; null = pas de bascule programmée
  announced_at?: string | null;
  public_at?: string | null;
  is_mystery?: boolean; // dérivé : palier === 'mystery'
}

// Helpers pour type guards
export function isMovement(element: WodElement): element is WodMovement {
  return element.type === WodElementType.MOVEMENT;
}

export function isGroup(element: WodElement): element is WodGroup {
  return element.type === WodElementType.GROUP;
}

// Utilitaires pour la structure
export class WodStructureUtils {

  // ---- Ladder (LOG-220) -------------------------------------------------

  /** Configuration ladder normalisée d'un groupe, ou null si ce n'en est pas un. */
  static ladderConfig(element: WodElement | null | undefined): LadderConfig | null {
    if (!element || !isGroup(element) || !element.ladder || typeof element.ladder !== 'object') return null;
    const raw = element.ladder;
    return {
      start: Math.max(Math.trunc(Number(raw.start) || 1), 1),
      increment: Math.max(Math.trunc(Number(raw.increment) || 1), 1),
    };
  }

  /** Reps du barreau `round` (1-indexé). */
  static ladderRung(cfg: LadderConfig, round: number): number {
    return cfg.start + (Math.max(round, 1) - 1) * cfg.increment;
  }

  /** « 1-2-3… », « 3-6-9… » — l'écriture tableau d'une suite sans fin. */
  static ladderDescription(cfg: LadderConfig): string {
    return `${[1, 2, 3].map(r => this.ladderRung(cfg, r)).join('-')}…`;
  }

  /** Somme des multiplicateurs (reps des feuilles × répétitions des sous-groupes). */
  static ladderRepsPerRung(group: WodGroup): number {
    const walk = (elements: WodElement[], factor: number): number =>
      elements.reduce((sum, el) => {
        if (isMovement(el)) return sum + Math.max(el.reps || 1, 1) * factor;
        if (isGroup(el)) return sum + walk(el.children, factor * Math.max(el.repetitions || 1, 1));
        return sum;
      }, 0);
    return walk(group.children, 1);
  }

  /** Premier groupe ladder d'une structure (profondeur d'abord), ou null. */
  static findLadderGroup(structure: WodStructure | null | undefined): WodGroup | null {
    const walk = (elements: WodElement[]): WodGroup | null => {
      for (const el of elements || []) {
        if (isGroup(el)) {
          if (this.ladderConfig(el)) return el;
          const nested = walk(el.children);
          if (nested) return nested;
        }
      }
      return null;
    };
    return structure ? walk(structure.elements) : null;
  }

  /**
   * Génère le texte d'affichage d'un mouvement.
   * Dans un groupe ladder, les reps sont un multiplicateur du barreau :
   * « Power Snatch » (×1, tu), « ×2 Overhead Squat » (×2).
   */
  static getMovementDisplayText(movement: WodMovement, inLadder = false): string {
    let text = movement.description;

    // « Max Pull-ups » : le nombre est le score de l'athlète.
    if (movement.isMaxReps) {
      text = `Max ${text}`;
    } else if (inLadder) {
      if ((movement.reps || 1) > 1) text = `×${movement.reps} ${text}`;
    } else if (movement.reps) {
      text = `${movement.reps} ${text}`;
    }

    const extras: string[] = [];
    // Charge prescrite — sauf si les notes la portent déjà (« 140kg/100kg »).
    const notesHaveWeight = /kg/i.test(movement.notes || '');
    if (movement.percentage_rm) {
      extras.push(
        movement.rm_reference
          ? `@ ${movement.percentage_rm} % 1RM ${movement.rm_reference}`
          : `@ ${movement.percentage_rm} % 1RM`
      );
    } else if (movement.weight_kg && !notesHaveWeight) {
      extras.push(
        movement.weight_kg_women
          ? `${movement.weight_kg}/${movement.weight_kg_women}kg`
          : `${movement.weight_kg}kg`
      );
    }
    if (movement.notes) extras.push(movement.notes);
    if (extras.length) text = `${text} (${extras.join(', ')})`;

    return text;
  }

  /**
   * Calcule le nombre total de mouvements dans un élément
   */
  static getTotalMovements(element: WodElement): number {
    if (isMovement(element)) {
      return 1;
    }

    if (isGroup(element)) {
      return element.children.reduce((sum, child) => sum + this.getTotalMovements(child), 0);
    }

    return 0;
  }

  /**
   * Calcule le nombre total de mouvements dans une structure complète
   */
  static getTotalMovementsInStructure(structure: WodStructure): number {
    return structure.elements.reduce((sum, el) => sum + this.getTotalMovements(el), 0);
  }

  /**
   * Calcule le nombre total de répétitions dans un élément
   */
  static getTotalReps(element: WodElement): number {
    if (isMovement(element)) {
      return element.reps || 0;
    }

    if (isGroup(element)) {
      if (this.ladderConfig(element)) return 0; // sans fin, comme un AMRAP
      const childReps = element.children.reduce((sum, child) => sum + this.getTotalReps(child), 0);
      return childReps * element.repetitions;
    }

    return 0;
  }

  /**
   * Calcule le nombre total de répétitions dans une structure complète
   */
  static getTotalRepsInStructure(structure: WodStructure): number {
    return structure.elements.reduce((sum, el) => sum + this.getTotalReps(el), 0);
  }

  /**
   * Génère un aperçu texte de la structure
   */
  static generatePreview(structure: WodStructure, indent = 0): string {
    const lines: string[] = [];
    const indentStr = '  '.repeat(indent);

    structure.elements.forEach(element => {
      if (isMovement(element)) {
        lines.push(`${indentStr}${this.getMovementDisplayText(element)}`);
      } else if (isGroup(element)) {
        // `name` (builder) ou `label` (« Athlete A » posé par WODEE) : un
        // groupe étiqueté s'affiche avec son étiquette, sinon deux « 1 Round »
        // anonymes ne disent pas qui fait quoi.
        const groupTitle = element.name || (element as any).label;
        const ladder = this.ladderConfig(element);
        const groupHeader = ladder
          ? `Ladder ${this.ladderDescription(ladder)}${groupTitle ? ` (${groupTitle})` : ''}:`
          : element.isAmrap
            ? `AMRAP${groupTitle ? ` (${groupTitle})` : ''}:`
            : `${element.repetitions} Round${element.repetitions > 1 ? 's' : ''}${groupTitle ? ` (${groupTitle})` : ''}:`;

        lines.push(`${indentStr}${groupHeader}`);

        // Récursif pour les enfants
        element.children.forEach(child => {
          if (isMovement(child)) {
            lines.push(`${indentStr}  ${this.getMovementDisplayText(child, ladder !== null)}`);
          } else if (isGroup(child)) {
            // Groupe imbriqué
            const nestedStructure: WodStructure = { elements: [child] };
            lines.push(this.generatePreview(nestedStructure, indent + 1));
          }
        });
      }
    });

    return lines.join('\n');
  }

  /**
   * Valide la structure et retourne les erreurs
   */
  static validateStructure(structure: WodStructure): string[] {
    const errors: string[] = [];

    // Règle 1: Au moins un élément
    if (!structure.elements || structure.elements.length === 0) {
      errors.push("Le WOD doit contenir au moins un mouvement ou groupe");
    }

    // Valider chaque élément récursivement
    structure.elements.forEach(element => {
      errors.push(...this.validateElement(element));
    });

    return errors;
  }

  /**
   * Valide un élément individuel (récursif)
   */
  private static validateElement(element: WodElement, path = ''): string[] {
    const errors: string[] = [];

    if (isMovement(element)) {
      if (!element.description || element.description.trim() === '') {
        errors.push(`Mouvement${path ? ` dans ${path}` : ''} sans description`);
      }
      if (element.reps !== undefined && element.reps < 1) {
        errors.push(`Mouvement "${element.description}"${path ? ` dans ${path}` : ''} doit avoir au moins 1 répétition`);
      }
    } else if (isGroup(element)) {
      const groupName = element.name || 'groupe sans nom';
      const currentPath = path ? `${path} > ${groupName}` : groupName;

      // Ladder (LOG-220) : départ et incrément ≥ 1 ; un ladder n'est pas un AMRAP.
      if (element.ladder) {
        const cfg = element.ladder;
        const label = element.name ? ` (${element.name})` : '';
        if (!(Number(cfg.start) >= 1)) errors.push(`Ladder${label} : le départ doit être ≥ 1`);
        if (!(Number(cfg.increment) >= 1)) errors.push(`Ladder${label} : l'incrément doit être ≥ 1`);
        if (element.isAmrap) errors.push(`Ladder${label} : un groupe ladder n'est pas un AMRAP (le ladder est déjà le schéma de tours)`);
      }

      if (element.repetitions < 1) {
        errors.push(`Le groupe "${groupName}" doit avoir au moins 1 répétition`);
      }

      if (!element.children || element.children.length === 0) {
        errors.push(`Le groupe "${groupName}" doit contenir au moins un élément`);
      } else {
        // Valider les enfants récursivement
        element.children.forEach(child => {
          errors.push(...this.validateElement(child, currentPath));
        });
      }
    }

    return errors;
  }

  /**
   * Convertit la structure en liste plate de mouvements (pour compatibilité)
   */
  static getFlatMovementsList(structure: WodStructure): string[] {
    const movements: string[] = [];

    function extractMovements(elements: WodElement[]) {
      elements.forEach(element => {
        if (isMovement(element)) {
          movements.push(element.description);
        } else if (isGroup(element)) {
          extractMovements(element.children);
        }
      });
    }

    extractMovements(structure.elements);
    return movements;
  }

  /**
   * Trouve un élément par ID dans la structure (récursif)
   */
  static findElementById(structure: WodStructure, id: string): WodElement | null {
    function search(elements: WodElement[]): WodElement | null {
      for (const element of elements) {
        if (element.id === id) {
          return element;
        }
        if (isGroup(element)) {
          const found = search(element.children);
          if (found) return found;
        }
      }
      return null;
    }

    return search(structure.elements);
  }

  /**
   * Supprime un élément de la structure par ID
   */
  static removeElement(structure: WodStructure, id: string): WodStructure {
    function filter(elements: WodElement[]): WodElement[] {
      return elements
        .filter(el => el.id !== id)
        .map(el => {
          if (isGroup(el)) {
            return {
              ...el,
              children: filter(el.children)
            };
          }
          return el;
        });
    }

    return {
      elements: filter(structure.elements)
    };
  }

  /**
   * Met à jour un élément dans la structure
   */
  static updateElement(structure: WodStructure, id: string, updates: Partial<WodElement>): WodStructure {
    function update(elements: WodElement[]): WodElement[] {
      return elements.map(el => {
        if (el.id === id) {
          return { ...el, ...updates };
        }
        if (isGroup(el)) {
          return {
            ...el,
            children: update(el.children)
          };
        }
        return el;
      });
    }

    return {
      elements: update(structure.elements)
    };
  }

  /**
   * Duplique un élément (avec de nouveaux IDs pour éviter conflits)
   */
  static duplicateElement(element: WodElement): WodElement {
    // Générer UUID v4
    const generateId = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    };

    if (isMovement(element)) {
      return {
        ...element,
        id: generateId()
      };
    }

    if (isGroup(element)) {
      return {
        ...element,
        id: generateId(),
        children: element.children.map(child => this.duplicateElement(child))
      } as WodGroup;
    }

    return element;
  }

  /**
   * Insère un élément dupliqué juste après l'original
   */
  static duplicateAndInsertAfter(structure: WodStructure, id: string): WodStructure {
    const insertAfter = (elements: WodElement[]): WodElement[] => {
      const result: WodElement[] = [];

      for (const el of elements) {
        result.push(el);

        if (el.id === id) {
          // Dupliquer et insérer juste après
          result.push(this.duplicateElement(el));
        }

        if (isGroup(el)) {
          // Mettre à jour les enfants récursivement
          result[result.length - 1] = {
            ...el,
            children: insertAfter(el.children)
          } as WodGroup;
        }
      }

      return result;
    };

    return {
      elements: insertAfter(structure.elements)
    };
  }

  /**
   * Réorganise les éléments en déplaçant un élément d'un index à un autre
   */
  static reorderElements(structure: WodStructure, fromIndex: number, toIndex: number): WodStructure {
    const elements = [...structure.elements];
    const [removed] = elements.splice(fromIndex, 1);
    elements.splice(toIndex, 0, removed);

    return {
      elements
    };
  }

  /**
   * Réorganise les enfants d'un groupe
   */
  static reorderGroupChildren(
    structure: WodStructure,
    groupId: string,
    fromIndex: number,
    toIndex: number
  ): WodStructure {
    function reorder(elements: WodElement[]): WodElement[] {
      return elements.map(el => {
        if (el.id === groupId && isGroup(el)) {
          const children = [...el.children];
          const [removed] = children.splice(fromIndex, 1);
          children.splice(toIndex, 0, removed);
          return {
            ...el,
            children
          };
        }
        if (isGroup(el)) {
          return {
            ...el,
            children: reorder(el.children)
          };
        }
        return el;
      });
    }

    return {
      elements: reorder(structure.elements)
    };
  }

  /**
   * Indente un élément (le rend enfant du groupe précédent)
   */
  static indentElement(structure: WodStructure, elementId: string): WodStructure {
    const indent = (elements: WodElement[]): WodElement[] => {
      const result: WodElement[] = [];

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];

        if (el.id === elementId && i > 0) {
          const previousSibling = result[result.length - 1];

          // L'élément précédent doit être un groupe
          if (isGroup(previousSibling)) {
            result[result.length - 1] = {
              ...previousSibling,
              children: [...previousSibling.children, el]
            } as WodGroup;
            continue; // Skip adding el to result
          }
        }

        // Récursif pour les groupes
        if (isGroup(el)) {
          result.push({
            ...el,
            children: indent(el.children)
          } as WodGroup);
        } else {
          result.push(el);
        }
      }

      return result;
    };

    return {
      elements: indent(structure.elements)
    };
  }

  /**
   * Désindente un élément (le sort d'un niveau vers le haut)
   */
  static outdentElement(structure: WodStructure, elementId: string): WodStructure {
    const outdent = (elements: WodElement[], parentIndex: number | null = null): {
      elements: WodElement[],
      extracted?: WodElement
    } => {
      const result: WodElement[] = [];
      let extracted: WodElement | undefined;

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];

        if (isGroup(el)) {
          const childResult = outdent(el.children, i);

          if (childResult.extracted) {
            // L'élément a été extrait d'un enfant
            result.push({
              ...el,
              children: childResult.elements
            } as WodGroup);

            // Insérer l'élément extrait juste après ce groupe
            if (parentIndex !== null) {
              extracted = childResult.extracted;
            } else {
              result.push(childResult.extracted);
            }
          } else {
            result.push(el);
          }
        } else if (el.id === elementId && parentIndex !== null) {
          // Cet élément doit être extrait
          extracted = el;
        } else {
          result.push(el);
        }
      }

      return { elements: result, extracted };
    };

    const { elements } = outdent(structure.elements, null);
    return { elements };
  }

  /**
   * Vérifie si un élément peut être indenté
   */
  static canIndent(structure: WodStructure, elementId: string): boolean {
    const check = (elements: WodElement[]): boolean => {
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];

        if (el.id === elementId) {
          // Peut indenter si l'élément précédent est un groupe
          if (i > 0 && isGroup(elements[i - 1])) {
            return true;
          }
          return false;
        }

        if (isGroup(el)) {
          if (check(el.children)) return true;
        }
      }
      return false;
    };

    return check(structure.elements);
  }

  /**
   * Vérifie si un élément peut être désindenté
   */
  static canOutdent(structure: WodStructure, elementId: string): boolean {
    const check = (elements: WodElement[], depth: number): boolean => {
      for (const el of elements) {
        if (el.id === elementId) {
          // Peut outdent si pas au niveau racine
          return depth > 0;
        }

        if (isGroup(el)) {
          if (check(el.children, depth + 1)) return true;
        }
      }
      return false;
    };

    return check(structure.elements, 0);
  }

  /**
   * Déplace un élément vers un groupe cible (via drag & drop)
   * Retire l'élément de son parent actuel et l'ajoute aux enfants du groupe cible
   */
  static moveElementToGroup(structure: WodStructure, elementId: string, targetGroupId: string): WodStructure {
    let movedElement: WodElement | null = null;

    // Étape 1 : Extraire l'élément de son emplacement actuel
    const removeElement = (elements: WodElement[]): WodElement[] => {
      const result: WodElement[] = [];

      for (const el of elements) {
        if (el.id === elementId) {
          movedElement = el;
          continue; // Skip cet élément
        }

        if (isGroup(el)) {
          const newChildren = removeElement(el.children);
          result.push({
            ...el,
            children: newChildren
          } as WodGroup);
        } else {
          result.push(el);
        }
      }

      return result;
    };

    // Étape 2 : Ajouter l'élément au groupe cible
    const addToTargetGroup = (elements: WodElement[]): WodElement[] => {
      return elements.map(el => {
        if (el.id === targetGroupId && isGroup(el) && movedElement) {
          return {
            ...el,
            children: [...el.children, movedElement]
          } as WodGroup;
        }

        if (isGroup(el)) {
          return {
            ...el,
            children: addToTargetGroup(el.children)
          } as WodGroup;
        }

        return el;
      });
    };

    const elementsWithoutMoved = removeElement(structure.elements);
    const finalElements = addToTargetGroup(elementsWithoutMoved);

    return {
      elements: finalElements
    };
  }

  /**
   * Déplace un élément vers la racine à un index spécifique
   * Retire l'élément de son parent actuel et l'insère à la racine
   */
  static moveElementToRoot(structure: WodStructure, elementId: string, targetIndex: number): WodStructure {
    let movedElement: WodElement | null = null;

    // Étape 1 : Extraire l'élément de son emplacement actuel
    const removeElement = (elements: WodElement[]): WodElement[] => {
      const result: WodElement[] = [];

      for (const el of elements) {
        if (el.id === elementId) {
          movedElement = el;
          continue; // Skip cet élément
        }

        if (isGroup(el)) {
          const newChildren = removeElement(el.children);
          result.push({
            ...el,
            children: newChildren
          } as WodGroup);
        } else {
          result.push(el);
        }
      }

      return result;
    };

    const elementsWithoutMoved = removeElement(structure.elements);

    if (!movedElement) {
      console.warn(`⚠️ Element ${elementId} not found for moveElementToRoot`);
      return structure; // Element not found
    }

    // Étape 2 : Insérer à l'index cible dans la racine
    const newElements = [...elementsWithoutMoved];
    newElements.splice(targetIndex, 0, movedElement);

    return {
      elements: newElements
    };
  }
}
